import React, { useEffect, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { addToDate, ChronoUnitLegacy } from "utils/time-utils";
import { InputSwitch } from "primereact/inputswitch";
import { T } from "services/lang/LanguageService";
import {
  useDeleteProductTemplateEntriesByFilter,
  useWipeAllProductTemplateEntries,
} from "api/api-hooks/product-templates-hooks";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import {
  IProductTemplateDropdownChangeEvent,
  ProductTemplateDropdown,
} from "../basic/dropdowns/product-template/ProductTemplateDropdown";
import ProductTemplatePreview from "../basic/product-template-preview/ProductTemplatePreview";
import CalendarWrapper from "../basic/calendar-wrapper/CalendarWrapper";
import { PermissionGuard } from "../wrappers/PermissionGuard";
import ConfirmDialog from "../basic/ConfirmDialog/ConfirmDialog";

const ProductTemplateDataManagerFragment = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [productTemplateParams, setProductTemplateParams] =
    useState<IProductTemplateDropdownChangeEvent>();
  const [filter, setFiler] = useState("");
  const [dateFrom, setDateFrom] = useState(addToDate(new Date(), -6, ChronoUnitLegacy.HOURS));
  const [dateTo, setDateTo] = useState(new Date());
  const [transposed, setTransposed] = useState(false);

  const [combinedData, setCombinedData] = useState<
    | { safeName: string; filter: string; dateFrom: Date; dateTo: Date; transposed: boolean }
    | undefined
  >();

  useEffect(() => {
    setCombinedData(undefined);
  }, [productTemplateParams]);

  const onFetchClick = () => {
    if (productTemplateParams?.refName) {
      setCombinedData({
        safeName: productTemplateParams.refName,
        filter,
        dateFrom,
        dateTo,
        transposed,
      });
    }
  };

  const onProductTemplateDropdownChange = (val: IProductTemplateDropdownChangeEvent) => {
    setProductTemplateParams(val);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const onFilterInputEnterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onFetchClick();
    }
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="flex gap-2 align-items-end flex-wrap">
        <div className="flex flex-column w-2">
          <b>{T("load-conf.product-template")}</b>
          <ProductTemplateDropdown
            value={productTemplateParams?.value}
            onChange={onProductTemplateDropdownChange}
          />
        </div>

        <div className="flex gap-2 flex-grow-1 flex-wrap">
          <div className="flex gap-2 align-items-end">
            <div className="flex flex-column">
              <div className="pl-2">
                <b>{T("word.from")}</b>
              </div>
              <CalendarWrapper value={dateFrom} onChange={setDateFrom} />
            </div>
            <div className="flex flex-column">
              <div className="pl-2">
                <b>{T("word.to")}</b>
              </div>

              <CalendarWrapper value={dateTo} onChange={setDateTo} />
            </div>
          </div>
          <div className="flex flex-column flex-grow-1">
            <b>{T("word.filter")}</b>
            <InputText
              ref={inputRef}
              value={filter}
              onChange={val => setFiler(val.target.value)}
              onKeyDown={onFilterInputEnterKeyDown}
              disabled={!productTemplateParams?.value}
              placeholder="Filter Expression"
            />
          </div>
        </div>

        <div className="flex flex-column">
          <b>{T("component.transposed")}</b>
          <div
            className="flex-grow-1 flex justify-content-center align-items-center"
            style={{ height: "37px" }}
          >
            <InputSwitch onChange={e => setTransposed(e.value)} checked={transposed} />
          </div>
        </div>

        <Button
          icon="pi pi-reply"
          className="p-button-success"
          label={T("word.preview")}
          onClick={() => onFetchClick()}
        />
      </div>

      {combinedData && (
        <div className="flex flex-column gap-2">
          <ProductTemplatePreview
            safeName={combinedData.safeName}
            filter={combinedData.filter}
            dateFrom={combinedData.dateFrom}
            dateTo={combinedData.dateTo}
            transposed={combinedData.transposed}
          />
          <div className="flex gap-2 justify-content-end">
            <DeleteFilteredEntries combinedData={combinedData} />
            <WipeAllEntries safeName={combinedData.safeName} />
          </div>
        </div>
      )}
    </div>
  );
};

const DeleteFilteredEntries = ({
  combinedData,
}: {
  combinedData: {
    safeName: string;
    filter: string;
    dateFrom: Date;
    dateTo: Date;
    transposed: boolean;
  };
}) => {
  const { mutate: deleteByFilter } = useDeleteProductTemplateEntriesByFilter({
    safeName: combinedData?.safeName || "",
  });

  const confirm = (event: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-filtered-entries"),
      icon: "pi pi-exclamation-triangle",
      accept: () =>
        deleteByFilter({
          filter: combinedData?.filter,
          dateFrom: combinedData?.dateFrom.toISOString(),
          dateTo: combinedData?.dateTo.toISOString(),
        }),
    });
  };

  return (
    <PermissionGuard permissions="easas:product-templates:data:delete-multiple">
      <div>
        <Button
          icon="pi pi-trash"
          className="p-button-danger"
          label={T("action.delete-filtered-entries")}
          onClick={e => confirm(e)}
        />
        <ConfirmPopup />
      </div>
    </PermissionGuard>
  );
};

const WipeAllEntries = ({ safeName }: { safeName: string }) => {
  const { mutate: deleteByFilter } = useWipeAllProductTemplateEntries({ safeName: safeName || "" });
  const [dialog, setDialog] = useState(false);

  return (
    <PermissionGuard permissions="easas:product-templates:data:delete-all">
      <div>
        <Button
          icon="pi pi-trash"
          className="p-button-danger"
          label={T("action.truncate-table")}
          onClick={() => setDialog(true)}
        />
        <ConfirmDialog
          visible={dialog}
          setVisible={setDialog}
          confirmedCallback={() => deleteByFilter({})}
          confirmMessage={T("confirm-dialog.wipe-all-entries")}
        />
      </div>
    </PermissionGuard>
  );
};

export default ProductTemplateDataManagerFragment;
